/* eslint-disable @typescript-eslint/no-explicit-any */
import type { HTMLAttributes } from "react";
import get from "lodash/get.js";
import {
  type ExtractRecordPaths,
  type HintedString,
  useFieldValue,
  useTranslate,
} from "ra-core";
import { cn } from "@/lib/utils";

import type { FieldProps } from "@/lib/field.type";
import { useSignertUrl } from "@/components/mimin/providers/commons/useSignertVedlegg";

/**
 * Displays an image or a list of images from a record field inside an img element or a ul of img elements.
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/imagefield/ ImageField documentation}
 *
 * @example
 * import { ImageField } from '@/components/admin';
 *
 * <ImageField
 *     source="avatar_url"
 *     className="[&_img]:w-8 [&_img]:h-8 [&_img]:rounded-full"
 *     empty={
 *         <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
 *           👤
 *         </div>
 *     }
 * />
 */
export const ImageField = <
  RecordType extends Record<string, any> = Record<string, any>,
>(
  props: ImageFieldProps<RecordType>,
) => {
  const { src, defaultValue, source, record, empty, title, ...rest } = props;
  const value = useFieldValue({ defaultValue, source, record });
  const titleValue =
    useFieldValue({
      ...props,
      // @ts-expect-error We ignore here because title might be a custom label or undefined instead of a field name
      source: title,
    })?.toString() ?? title;
  const translate = useTranslate();

  // the field may render either an empty element, an image, or a ul.
  // We choose to always apply the rest props to an enclosing span
  // to allow styling each case.
  if (value == null) {
    if (!empty) {
      return null;
    }
    return (
      <span {...rest} className={cn("image-empty", rest.className)}>
        {typeof empty === "string" ? translate(empty, { _: empty }) : empty}
      </span>
    );
  }

  if (Array.isArray(value)) {
    return (
      <span {...rest} className={cn("image-list", rest.className)}>
        <ul>
          {value.map((file, index) => {
            const fileTitleValue = title ? get(file, title, title) : title;
            const srcValue = src ? get(file, src, title) : title;

            return (
              <li key={index}>
                <Bilde src={srcValue} title={fileTitleValue} />
              </li>
            );
          })}
        </ul>
      </span>
    );
  }

  return (
    <span {...rest} className={cn("image-single", rest.className)}>
      <Bilde src={value?.toString()} title={titleValue} />
    </span>
  );
};

/**
 * Ett bilde, med signering av det som ligger i den private vedleggsbøtta.
 *
 * MIMIN-TILLEGG i en ellers uendret komponent fra shadcn-admin-kit.
 * `useSignertUrl` sender alt som IKKE peker inn i bøtta uendret tilbake —
 * eksterne adresser, `blob:` fra en fil som nettopp ble valgt, avatarer fra
 * andre tjenester. Derfor er dette trygt for alle bruk av ImageField, ikke
 * bare produktbildet.
 */
const Bilde = ({ src, title }: { src?: string; title?: string }) => {
  const signert = useSignertUrl(src);
  if (!signert) return null;
  return <img title={title} alt={title} src={signert} />;
};

// What? TypeScript loses the displayName if we don't set it explicitly
ImageField.displayName = "ImageField";

export interface ImageFieldProps<
  RecordType extends Record<string, any> = Record<string, any>,
> extends FieldProps<RecordType>,
    HTMLAttributes<HTMLSpanElement> {
  defaultValue?: any;
  src?: string;
  title?: HintedString<ExtractRecordPaths<RecordType>>;
}
